import { useState, useMemo } from "react";

export const useTableColumns = (columns, options = {}) => {
  const { minVisibleColumns = 2 } = options;

  // Initialize visibility state from columns
  const initialVisibility = useMemo(() => {
    const visibility = {};
    columns?.forEach?.((col) => {
      if (col.filterable || (col?.filterable ?? true)) {
        visibility[col.key] = true && (col?.visible ?? true); // Default all visible
      }
    });
    return visibility;
  }, [columns]);

  const [visibleColumns, setVisibleColumns] = useState(initialVisibility);

  // Get filterable columns
  const filterableColumns = useMemo(
    () => columns?.filter?.((col) => col.filterable !== false),
    [columns]
  );

  // Get visible columns (including non-filterable ones)
  const visibleColumnsList = useMemo(
    () =>
      columns?.filter?.((col) => {
        if (col.filterable === false) {
          return col?.visible ?? true;
        }
        return visibleColumns[col.key];
      }),
    [columns, visibleColumns]
  );

  // Count visible filterable columns
  const visibleCount = useMemo(
    () => Object.values(visibleColumns).filter(Boolean).length,
    [visibleColumns]
  );

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    // Prevent hiding if at minimum
    if (visibleColumns[columnKey] && visibleCount <= minVisibleColumns) {
      return;
    }

    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Check if column should be disabled
  const isColumnDisabled = (columnKey) => {
    return visibleColumns[columnKey] && visibleCount <= minVisibleColumns;
  };

  // Reset to default (all visible)
  const resetColumns = () => {
    setVisibleColumns(initialVisibility);
  };

  return {
    visibleColumns,
    visibleColumnsList,
    filterableColumns,
    visibleCount,
    toggleColumn,
    isColumnDisabled,
    resetColumns,
    minVisibleColumns,
  };
};
